import getLocal from '@/app/utils/getLocal';
import { loginUser } from '@/utils/cookie';
import gtm from '@/utils/gtm';
import loginURL from '@/utils/loginURL';
import { getIsPadWidth } from '@elem/utils/devices';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { OuterRingIcon, RobotIcon } from './icons';
import styles from './index.module.scss';

const langAppId = {
    de: '0a1e5fb9-d869-4ef6-8aa0-983e24310671',
    en: '92b9f6ac-6623-44f2-994c-a8dd0a8c28f4',
    us: '96d60423-a273-4d0e-be4e-161d14f3fa8e',
    au: 'a0758237-aa5d-4bd6-a9d7-6c334a9fbd06',
    za: 'e046d631-3da3-466d-ac90-eaf50baef4ee',
    fr: 'e7387205-6a17-44f3-8b7c-32a67b0df84b',
    jp: '2de0e8ed-b74e-40d0-8394-c32a2c70f938',
    cn: 'a6210c8b-db95-4a5f-80d4-cd8caf849e1c',
    kr: '813a2f6c-08f7-48dd-a807-7d6069d957ec',
};

const appIdMap = {
    de: langAppId.de,
    us: langAppId.us,
    ca: langAppId.en,
    au: langAppId.au,
    za: langAppId.za,
    uk: langAppId.en,
    eu: langAppId.en,
    kr: langAppId.kr,
    fr: langAppId.fr,
    jp: langAppId.jp,
    cn: langAppId.cn,
};

const regionCode = {
    us: 1,
    ca: 2,
    au: 3,
    za: 4,
    uk: 5,
    eu: 6,
    de: 7,
    kr: 8,
    fr: 12,
    cn: 13,
    jp: 14,
};

const noV2Map: any = ['fr', 'jp', 'cn'];
// 采用新聊天窗窗样式的区域
const newChatBoxStyleSite = ['us', 'ca', 'au', 'za', 'uk', 'eu', 'de', 'fr', 'jp', 'cn', 'kr'];

const newChatBoxIcon = ['us', 'ca', 'au', 'za', 'uk', 'eu', 'de', 'fr', 'jp', 'cn', 'kr'];

const langTxt: any = {
    us: `I'm online! Your Intelligent assistant is here ✨`,
    en: `I'm online! Your AI assistant is here ✨`,
    de: 'Ich bin online! Ihr KI-Assistent ist da ✨',
    jp: 'オンラインになりました！AIサポート登場 ✨',
    cn: '我上线啦！AI客服上线报道 ✨',
    fr: 'Je suis en ligne ! Votre assistant IA est là ✨',
    kr: '안녕하세요! 인공지능 대기 중입니다',
};

const regionUrl: any = {
    fr: 'https://m-9ae05e11-c6d8-ed11-aed0-000d3a64d144.eu.omnichannelengagementhub.com',
    jp: 'https://m-9ae05e11-c6d8-ed11-aed0-000d3a64d144.eu.omnichannelengagementhub.com',
    cn: 'https://m-9ae05e11-c6d8-ed11-aed0-000d3a64d144.eu.omnichannelengagementhub.com',
    kr: 'https://m-9ae05e11-c6d8-ed11-aed0-000d3a64d144.eu.omnichannelengagementhub.com',
    other: 'https://unq9ae05e11c6d8ed11aed0000d3a64d-crm4.omnichannelengagementhub.com',
};

const jpChatStyles = {
    confirmationPaneProps: {
        controlProps: {
            titleText: 'チャットを終了してもよろしいですか',
            hideSubtitle: true,
            confirmButtonText: 'はい',
            cancelButtonText: 'いいえ',
        },
        styleProps: {
            confirmButtonStyleProps: {
                fontSize: '12px',
            },
            cancelButtonStyleProps: {
                fontSize: '12px',
            },
        },
    },
};

function MicrosoftChat({ payloadData }: any) {
    const [isLoaded, setIsLoaded] = useState(false);
    const isStartChat = useRef<boolean>(false);
    const [initState, setInitState] = useState<boolean>(false);

    const userInfo = loginUser();
    const { locale } = getLocal();
    const isMobile = getIsPadWidth(); //是否是移动端

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.lcw = () => {
            //会话前调查自适应卡片
            return {
                //注入payload
                preChatSurveyPaneProps: {
                    controlProps: {
                        payload: JSON.stringify(payloadData),
                    },
                },
                webChatContainerProps: {
                    renderingMiddlewareProps: {
                        systemMessageBoxStyles: {
                            maxWidth: '510px', //系统文本宽度（默认的padding 10px）
                        },
                        receivedMessageAnchorStyles: {
                            color: 'blue', //链接颜色
                        },
                    },
                    adaptiveCardStyles: {
                        background: 'white', //自适应卡片背景颜色
                        color: 'black', //自适应卡片字体颜色
                    },
                    webChatStyles: {
                        bubbleBackground: 'white', //系统背景颜色
                        bubbleTextColor: 'black', //系统字体颜色
                        bubbleFromUserBackground: '#94ed69', //用户输入文本背景颜色
                        bubbleFromUserTextColor: 'black', //用户输入字体颜色
                        bubbleMaxWidth: 500, //系统聊天文本最大宽度（默认250）
                    },
                },
                ...(locale === 'jp' ? jpChatStyles : {}),
            };
        };

        function contextProvider() {
            const data = {
                SiteUrl: { value: window.location.origin + (loginURL() || ''), isDisplayable: true },
                SiteName: { value: (regionCode as any)[locale || ''] || '', isDisplayable: true },
                Origin: { value: 'Official website', isDisplayable: true },
                ToAgent: {
                    value: 'false',
                    isDisplayable: true,
                },
            };
            //Here it is assumed that the corresponding work stream would have context variables with logical name of 'contextKey1', 'contextKey2', 'contextKey3'. If no context variable exists with a matching logical name, items are created assuming Type:string
            const res = Object.assign(
                data,
                userInfo && userInfo.user
                    ? {
                          Email: { value: userInfo.user.email, isDisplayable: true }, // boolean value
                      }
                    : {},
            );
            return res;
        }

        window.addEventListener('lcw:ready', function handleLivechatReadyEvent() {
            // Handle LiveChat Ready event
            // SDK methods are ready for use now
            // Setting custom context provider to be used with Chat for Dynamics 365
            // The custom context provided by custom context provider can be used for routing the chat to a particular queue
            (window as any).Microsoft.Omnichannel.LiveChatWidget.SDK.setContextProvider(contextProvider);
            // Starting a new chat
            //Microsoft.Omnichannel.LiveChatWidget.SDK.startChat();
            setIsLoaded(true);
        });
        window.addEventListener('lcw:startChat', function handleLivechatStartedEvent() {
            // Handle the live chat started event
        });
        window.addEventListener('lcw:closeChat', function handleLivechatClosedEvent() {
            // Handle the live chat closed event
            setInitState(false);
        });
        window.addEventListener('lcw:onMessageReceived', function handleWidgetMessageReceivedEvent(payload) {
            isStartChat.current = true;
            setInitState(true);
            // Handle the live chat widget message sent event
        });
        window.addEventListener('lcw:onMinimize', function handleWidgetMinimizeEvent() {
            // Handle the live chat widget minimize event

            if (!isStartChat.current) {
                (window as any).Microsoft.Omnichannel.LiveChatWidget.SDK.closeChat();
            }
        });
    }, []);

    const open = () => {
        if (isStartChat) {
            (window as any).Microsoft.Omnichannel.LiveChatWidget.SDK.startChat();
            return;
        }
        if (!initState) {
            (window as any).Microsoft.Omnichannel.LiveChatWidget.SDK.startChat();
            setInitState(true);
        } else {
            (window as any).Microsoft.Omnichannel.LiveChatWidget.SDK.closeChat();
            setInitState(false);
        }
    };

    const appId = (appIdMap as any)[locale || ''];
    const orgUrl = regionUrl[locale] || regionUrl['other'];

    const errorFun = `(function(el){el.parentNode.removeChild(el);var s=document.createElement('script');s.src='https://ocprodpubliceurgs.blob.core.windows.net/livechatwidget/scripts/LiveChatBootstrapper.js';s.setAttribute('id', 'Microsoft_Omnichannel_LCWidget');s.setAttribute('data-app-id', ${appId});s.setAttribute('data-lcw-version', 'prod');s.setAttribute('data-org-id', '9ae05e11-c6d8-ed11-aed0-000d3a64d144');s.setAttribute('data-org-url', ${orgUrl});document.body.appendChild(s);})(this);`;

    return (
        <>
            <Helmet>
                {
                    appId && (
                        <script
                            v2={!noV2Map.includes(locale)}
                            id="Microsoft_Omnichannel_LCWidget"
                            src="https://oc-cdn-public-eur.azureedge.net/livechatwidget/scripts/LiveChatBootstrapper.js"
                            onError={errorFun}
                            data-app-id={appId}
                            data-lcw-version="prod"
                            data-org-id="9ae05e11-c6d8-ed11-aed0-000d3a64d144"
                            data-org-url={orgUrl}
                            data-hide-chat-button="true"
                            defer
                            data-customization-callback={newChatBoxStyleSite.includes(locale) ? 'lcw' : ''}
                        />
                    )

                    // 测试
                    // <script
                    //     id="Microsoft_Omnichannel_LCWidget"
                    //     src="https://oc-cdn-public-eur.azureedge.net/livechatwidget/scripts/LiveChatBootstrapper.js"
                    //     data-app-id="0615f1d5-5bd6-41c5-b60f-067922e2b8f6"
                    //     data-lcw-version="prod"
                    //     data-org-id="d4ba8562-b7da-ed11-a80b-000d3a64d779"
                    //     data-org-url="https://unqd4ba8562b7daed11a80b000d3a64d-crm4.omnichannelengagementhub.com"
                    //     data-hide-chat-button="true"
                    //     defer
                    //     data-customization-callback={locale === 'eu' ? 'lcw' : ''}
                    // />
                }
            </Helmet>
            {isLoaded && (
                <div
                    onClick={open}
                    data-btn-id="MicrosoftChat-icon"
                    className={`MicrosoftChat ${newChatBoxIcon.includes(locale) ? styles.CompA : styles.CompB} GtmModuleData-btn-common `}
                >
                    {!newChatBoxIcon.includes(locale) ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M3 5C3 4.44771 3.44772 4 4 4H16C16.5523 4 17 4.44772 17 5V13.5625C17 14.1148 16.5523 14.5625 16 14.5625H9.125L5.625 17V14.5625H4C3.44772 14.5625 3 14.1148 3 13.5625V5Z"
                                stroke="black"
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M16.625 11H20C20.5523 11 21 11.4477 21 12V17.3125C21 17.8648 20.5523 18.3125 20 18.3125H19.125V20L16.625 18.3125H12C11.4477 18.3125 11 17.8648 11 17.3125V15"
                                stroke="black"
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                            />
                        </svg>
                    ) : (
                        <div
                            className={styles['chat-box']}
                            onClick={() => {
                                gtm.push({
                                    event: 'uaEvent',
                                    event_name: 'get_support',
                                    button_name: 'Support Chat Icon',
                                    __btn_id1: `MicrosoftChat`,
                                    __btn_id2: 'MicrosoftChat-icon',
                                    __btn_type: 'common',
                                });
                            }}
                        >
                            <div className={styles['out-ring']}>
                                <div className={styles['out-ring-icon']}>
                                    <OuterRingIcon />
                                </div>
                                <div className={styles['robot-icon']}>
                                    <RobotIcon />
                                </div>
                            </div>
                            <div className={styles['chat-tips']}>
                                <span className={styles['chat-tips-txt']}>{langTxt[locale] || langTxt['en']}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

export default dynamic(() => Promise.resolve(MicrosoftChat), {
    ssr: false,
});
